// react_frontend/src/Components/Modules/Sales/Quotations.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { FaPlus, FaTrash, FaFileInvoice, FaCheck } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function Quotations() {
    const [quotations, setQuotations] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [items, setItems] = useState([{ item_name: '', quantity: 1, unit_price: 0 }]);
    const [form, setForm] = useState({ customer_id: '', quotation_date: new Date().toISOString().split('T')[0], expiry_date: '', notes: '' });
    const [customers, setCustomers] = useState([]);

    const fetchData = async () => {
        const [qRes, cRes] = await Promise.all([
            AxiosAuthInstance.get('quotations'),
            AxiosAuthInstance.get('customers') // Adjust endpoint if needed
        ]);
        setQuotations(qRes.data.quotations || []);
        setCustomers(cRes.data.customers || cRes.data || []);
    };

    useEffect(() => { fetchData(); }, []);

    const addItem = () => setItems([...items, { item_name: '', quantity: 1, unit_price: 0 }]);
    const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
    const updateItem = (idx, field, val) => {
        const newItems = [...items];
        newItems[idx][field] = val;
        newItems[idx].total = newItems[idx].quantity * newItems[idx].unit_price;
        setItems(newItems);
    };

    const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

    const handleSave = async () => {
        try {
            await AxiosAuthInstance.post('quotations', { ...form, items });
            Swal.fire({ icon: 'success', title: 'Quotation Created!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            setItems([{ item_name: '', quantity: 1, unit_price: 0 }]);
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleConvert = async (id) => {
        const result = await Swal.fire({ title: 'Convert to Billing?', text: 'This will create a Sales Order/Bill.', icon: 'question', showCancelButton: true });
        if (result.isConfirmed) {
            try {
                const res = await AxiosAuthInstance.post(`quotations/${id}/convert`);
                Swal.fire({ icon: 'success', title: 'Converted!', text: `Billing ID: ${res.data.billing_id}`, timer: 2000 });
                fetchData();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed' });
            }
        }
    };

    const statusColor = { draft: 'default', sent: 'info', accepted: 'success', rejected: 'error', converted: 'primary' };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Quotations" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Sales Quotations</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>New Quotation</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Quotation #</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {quotations.map((q) => (
                            <TableRow key={q.id} hover>
                                <TableCell><code>{q.quotation_number}</code></TableCell>
                                <TableCell>{q.customer?.name || 'Walk-in'}</TableCell>
                                <TableCell>{q.quotation_date}</TableCell>
                                <TableCell align="right">৳{Number(q.total_amount).toLocaleString()}</TableCell>
                                <TableCell><Chip label={q.status} size="small" color={statusColor[q.status]} /></TableCell>
                                <TableCell align="center">
                                    {q.status !== 'converted' && (
                                        <IconButton size="small" onClick={() => handleConvert(q.id)} sx={{ color: '#4caf50' }} title="Convert to Billing">
                                            <FaCheck />
                                        </IconButton>
                                    )}
                                    {q.status === 'draft' && (
                                        <IconButton size="small" onClick={async () => {
                                            if(await Swal.fire({title: 'Delete?', icon: 'warning', showCancelButton: true}).then(r => r.isConfirmed)) {
                                                await AxiosAuthInstance.delete(`quotations/${q.id}`);
                                                fetchData();
                                            }
                                        }} sx={{ color: '#f44336' }}><FaTrash /></IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Quotation</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Customer</InputLabel>
                            <Select value={form.customer_id} label="Customer" onChange={(e) => setForm({...form, customer_id: e.target.value})}>
                                <MenuItem value="">Walk-in Customer</MenuItem>
                                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name} ({c.phone})</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Date" type="date" size="small" value={form.quotation_date} onChange={(e) => setForm({...form, quotation_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                            <TextField label="Expiry Date" type="date" size="small" value={form.expiry_date} onChange={(e) => setForm({...form, expiry_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                        </Box>
                        
                        <Typography variant="subtitle2" fontWeight={600} sx={{mt: 1}}>Items</Typography>
                        {items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField size="small" label="Item Name" value={item.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} sx={{flex: 3}} />
                                <TextField size="small" type="number" label="Qty" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} sx={{flex: 1}} />
                                <TextField size="small" type="number" label="Price" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} sx={{flex: 1}} />
                                <TextField size="small" label="Total" value={(item.quantity * item.unit_price).toFixed(2)} disabled sx={{flex: 1}} />
                                <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}><FaTrash size={14} /></IconButton>
                            </Box>
                        ))}
                        <Button variant="outlined" size="small" startIcon={<FaPlus />} onClick={addItem} sx={{alignSelf: 'flex-start'}}>Add Item</Button>
                        
                        <Box sx={{ p: 2, bgcolor: '#f0f4f8', borderRadius: 1, textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight={700}>Total: ৳{totalAmount.toLocaleString()}</Typography>
                        </Box>
                        <TextField label="Notes" multiline rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save Quotation</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}